import type { ReactNode } from 'react'
import {
  Box,
  VStack,
  Flex,
  Icon,
  IconButton,
  Tooltip,
  useColorMode,
  Spacer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
} from '@chakra-ui/react'
import {
  FilePlus,
  Upload,
  Link as LinkIcon,
  Plus,
  ChevronRight,
  Settings,
  Sun,
  Moon,
} from 'lucide-react'

interface CollapsedSidebarProps {
  isCollapsed: boolean
  onToggleCollapse: () => void
  onNewFile: () => void
  onSettings?: () => void
  handleFileUpload: () => void
  onUrlOpen: () => void
  userMenu: ReactNode
}

export function CollapsedSidebar({
  isCollapsed,
  onToggleCollapse,
  onNewFile,
  onSettings,
  handleFileUpload,
  onUrlOpen,
  userMenu,
}: CollapsedSidebarProps) {
  const { colorMode, toggleColorMode } = useColorMode()

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      bottom={0}
      w="48px"
      bg={colorMode === 'dark' ? '#0c0a09' : 'white'}
      display="flex"
      flexDirection="column"
      alignItems="center"
      overflow="hidden"
      opacity={isCollapsed ? 1 : 0}
      pointerEvents={isCollapsed ? 'auto' : 'none'}
      transition="opacity 0.2s ease"
    >
      {/* Logo and expand button */}
      <VStack spacing={0} w="full" flexShrink={0}>
        <Flex
          h="45px"
          minH="45px"
          maxH="45px"
          w="full"
          align="center"
          justify="center"
          borderBottomWidth="1px"
          borderColor={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
          bg={colorMode === 'dark' ? '#0c0a09' : 'white'}
        >
          <Tooltip label="Expand sidebar" placement="right">
            <IconButton
              aria-label="Expand sidebar"
              icon={<Icon as={ChevronRight} boxSize={4} />}
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
            />
          </Tooltip>
        </Flex>
      </VStack>
      <VStack spacing={1} flex={1} py={2}>
        <Menu placement="right-start">
          <Tooltip label="New" placement="right">
            <MenuButton
              as={IconButton}
              aria-label="New"
              icon={<Icon as={Plus} boxSize={4} />}
              variant="ghost"
              size="sm"
            />
          </Tooltip>
          <Portal>
            <MenuList>
              <MenuItem
                icon={<Icon as={FilePlus} boxSize={4} />}
                onClick={onNewFile}
              >
                New file
              </MenuItem>
              <MenuItem
                icon={<Icon as={Upload} boxSize={4} />}
                onClick={handleFileUpload}
              >
                Browse files
              </MenuItem>
              <MenuItem
                icon={<Icon as={LinkIcon} boxSize={4} />}
                onClick={onUrlOpen}
              >
                From URL
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      </VStack>
      <Spacer />
      <Box px={1} py={2}>
        <Tooltip
          label={colorMode === 'dark' ? 'Light mode' : 'Dark mode'}
          placement="right"
        >
          <IconButton
            aria-label={colorMode === 'dark' ? 'Light mode' : 'Dark mode'}
            icon={<Icon as={colorMode === 'dark' ? Sun : Moon} boxSize={4} />}
            variant="ghost"
            size="sm"
            onClick={toggleColorMode}
          />
        </Tooltip>
      </Box>
      {onSettings && (
        <Box px={1} py={2}>
          <Tooltip label="Share settings defaults" placement="right">
            <IconButton
              aria-label="Share settings defaults"
              icon={<Icon as={Settings} boxSize={4} />}
              variant="ghost"
              size="sm"
              colorScheme="brand"
              onClick={onSettings}
            />
          </Tooltip>
        </Box>
      )}
      <Box px={1} py={2}>
        {userMenu}
      </Box>
    </Box>
  )
}
